// src/contexts/PermissionsContext.tsx

import { createContext, useContext, ReactNode } from "react";
import { Role, Action, usePermissions } from "@/hooks/usePermissions";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { modulesTypes, actions } from "@/convex/system/permissions/";
import { UserContext } from "@/convex/system/context";
type ActionType = typeof actions.type;
type Module = typeof modulesTypes.type;

// Tipo para el contexto retornado por checkUserContext
type CheckUserContextResult =
  | {
      userId: Id<"users">;
      companyId?: Id<"companies">;
      branchId?: Id<"branches">;
      activeBranchId?: Id<"branches">;
      availableBranches: Array<{
        _id: Id<"branches">;
        name: string;
        code?: string;
      }>;
      role?: Doc<"roles">;
      roleId?: Id<"roles">;
      profile: {
        _id: Id<"users">;
        name: string;
        email: string;
        image?: string;
      };
      permissions: Array<{
        _id: Id<"permissions">;
        module: string;
      }>;
      subscription?: {
        status: string;
        isTrial: boolean;
        isGracePeriod: boolean;
        isActive: boolean;
        enabledModules: string[];
        limits: {
          maxUsers: number;
          maxBranches: number;
          storageGB: number;
          invoicesPerMonth: number;
        };
        currentUsage: {
          userCount: number;
          branchCount: number;
          storageUsedGB: number;
          invoicesThisMonth: number;
        };
      };
      isSuperAdmin: boolean;
    }
  | null
  | { error: string; type: string }
  | undefined;

interface PermissionsContextType {
  can: (action: ActionType, module?: Module) => boolean;
  canAny: (actions: ActionType[], module?: Module) => boolean;
  canAll: (actions: ActionType[], module?: Module) => boolean;
  getPermission: (module?: Module) => Doc<"permissions"> | null | undefined;
  getAllowedActions: (module?: Module) => Action[];
  isLoading: boolean;
  userContext: CheckUserContextResult;
  userRole: Doc<"roles"> | undefined;
  hasRole: boolean;
  currentUser: CheckUserContextResult;
  permissions: Array<Doc<"permissions">> | undefined;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(
  undefined,
);

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const permissionsData = usePermissions();

  const contextValue: PermissionsContextType = {
    can: permissionsData.can,
    canAny: permissionsData.canAny,
    canAll: permissionsData.canAll,
    getPermission: permissionsData.getPermission,
    getAllowedActions: permissionsData.getAllowedActions,
    isLoading: permissionsData.isLoading,
    userContext: permissionsData.userContext,
    userRole: permissionsData.userRole,
    hasRole: permissionsData.hasRole,
    currentUser: permissionsData.currentUser,
    permissions: permissionsData.permissions,
  };

  return (
    <PermissionsContext.Provider value={contextValue}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissionsContext() {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error(
      "usePermissionsContext must be used within PermissionsProvider",
    );
  }
  return context;
}

export type { PermissionsContextType };
